import {
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import CustomButton from "../../../components/CustomButton";
import Select from "../../../components/Select/Select";
import { getStockReorderLevel } from "../../../redux/reducers/clinicSlice";
import { isActiveRecord } from "../../../util/activeStatus";
import { AppColors } from "../../../util/AppColors";
import { tblGrayBack } from "../../../util/object";

const tableHeaders = [
  { key: "tradeName", label: "Trade Name" },
  { key: "productName", label: "Generic Name" },
  { key: "category", label: "Category" },
  { key: "quantity", label: "Unit Qty" },
  { key: "reorderLevel", label: "Threshold Qty" },
  { key: "reorderQty", label: "Reorder Qty" },
];

const initFilters = {
  category: null,
  subCategory: null,
  supplier: null,
};

const displayCell = (value) =>
  value == null || value === "" ? "-" : String(value);

const toOptionList = (list = []) =>
  (Array.isArray(list) ? list : [])
    .filter((item) => item?.name && isActiveRecord(item))
    .map((item) => ({
      name: item.name,
      value: item.name,
      id: item.id,
      subCategories: Array.isArray(item.subCategories)
        ? item.subCategories
        : [],
    }));

const ReorderList = () => {
  const dispatch = useDispatch();
  const requestRef = useRef(0);
  const [filters, setFilters] = useState(initFilters);
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const mandateDataRaw = sessionStorage.getItem("mandateData");

  const { stockCategories, suppliers } = useMemo(() => {
    if (!mandateDataRaw) return { stockCategories: [], suppliers: [] };
    try {
      const reqData = JSON.parse(mandateDataRaw);
      const categories = Array.isArray(reqData?.categories)
        ? reqData.categories
        : [];
      return {
        stockCategories: categories
          .filter(
            (cat) => cat?.type === "stock" && cat?.name && isActiveRecord(cat),
          )
          .map((cat) => ({
            name: cat.name,
            value: cat.name,
            id: cat.id,
            subCategories: Array.isArray(cat.subCategories)
              ? cat.subCategories
              : [],
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
        suppliers: (Array.isArray(reqData?.suppliers) ? reqData.suppliers : [])
          .filter((sup) => sup?.supplierName && isActiveRecord(sup))
          .map((sup) => ({
            name: sup.supplierName,
            value: sup.supplierName,
            id: sup.supplierId,
          }))
          .sort((a, b) => a.name.localeCompare(b.name)),
      };
    } catch {
      return { stockCategories: [], suppliers: [] };
    }
  }, [mandateDataRaw]);

  const subCategoryList = useMemo(() => {
    if (!filters?.category) return [];
    const matched =
      stockCategories.find(
        (cat) =>
          cat?.value === filters.category?.value ||
          cat?.name === filters.category?.name,
      ) ?? filters.category;
    return toOptionList(matched?.subCategories).sort((a, b) =>
      a.name.localeCompare(b.name),
    );
  }, [filters?.category, stockCategories]);

  useEffect(() => {
    const requestId = ++requestRef.current;
    setLoaded(false);
    dispatch(
      getStockReorderLevel({
        category: filters?.category?.value ?? "",
        subCategory: filters?.subCategory?.value ?? "",
        supplier: filters?.supplier?.value ?? "",
      }),
    ).then((res) => {
      if (requestId !== requestRef.current) return;
      setRows(Array.isArray(res?.payload) ? res.payload : []);
      setLoaded(true);
    });
  }, [filters, dispatch]);

  const onChangeFilter = (name, value) => {
    const nextValue = value ?? null;
    setFilters((prev) => {
      if (name === "category") {
        return { ...prev, category: nextValue, subCategory: null };
      }
      return { ...prev, [name]: nextValue };
    });
  };

  const onReset = () => {
    setFilters(initFilters);
  };

  return (
    <Container maxWidth="xl">
      <div className="ph20 mt15">
        <div className="flex-row-ali-cen flex-center">
          <div className="w20Per">
            <Select
              list={stockCategories}
              value={filters?.category}
              handleChange={(e, value) => onChangeFilter("category", value)}
              label="Category"
              search
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div className="w20Per ml15">
            <Select
              list={subCategoryList}
              value={filters?.subCategory}
              handleChange={(e, value) => onChangeFilter("subCategory", value)}
              label="Subcategory"
              search
              disabled={!filters?.category}
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div className="w20Per ml15">
            <Select
              list={suppliers}
              value={filters?.supplier}
              handleChange={(e, value) => onChangeFilter("supplier", value)}
              label="Supplier"
              search
              backgroundColor={AppColors.lightBlue2}
            />
          </div>
          <div className="ml15">
            <CustomButton text="Reset" onClick={onReset} smallBtn redBtn />
          </div>
        </div>
        <Typography className="font-medium fs12 gray28 mt10">
          Number of Stocks: {loaded ? rows.length : 0}
        </Typography>
        <TableContainer sx={{ maxHeight: "55vh" }} className="mt10">
          <Table aria-label="collapsible table" stickyHeader>
            <TableHead>
              <TableRow>
                {tableHeaders.map((header) => (
                  <TableCell
                    key={header.key}
                    style={tblGrayBack}
                    className="txt-semi-bold fs12"
                  >
                    {header.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loaded && rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={tableHeaders.length} className="h100">
                    <div className="no-rec">No records available</div>
                  </TableCell>
                </TableRow>
              ) : (
                rows.map((row, index) => (
                  <TableRow key={`${row?.stockId ?? "stock"}_${index}`}>
                    {tableHeaders.map((header) => (
                      <TableCell key={header.key} className="fs12">
                        {displayCell(row?.[header.key])}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </Container>
  );
};

export default ReorderList;
